import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Button,
  Box,
  Container,
  TextField,
  Paper,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { testAPI } from '../services/apiService';

const CreateTestPage: React.FC = () => {
  const navigate = useNavigate();
  const { setError } = useAuth();
  const [testName, setTestName] = useState('');
  const [timeLimitMinutes, setTimeLimitMinutes] = useState<number>(45); // Default 45 minutes
  const [nameError, setNameError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setLocalError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!testName.trim()) {
      setNameError('Test name is required');
      return;
    }
    
    setNameError('');
    setLocalError(null);
    setIsLoading(true);
    
    try {
      const createdTest = await testAPI.create({ 
        name: testName.trim(),
        time_limit_minutes: timeLimitMinutes
      });
      // Navigate to the test detail page
      navigate(`/tests/${createdTest.uuid}`);
    } catch (error: any) {
      let errorMessage = 'Error creating test';
      
      // Check if this is a usage limit error (HTTP 429)
      if (error?.status === 429 || error?.message?.includes('monthly test creation limit')) {
        errorMessage = 'You\'ve reached your monthly test creation limit. Please upgrade your subscription plan to create more tests.';
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      setLocalError(errorMessage);
      setError(errorMessage);
      console.error('Error creating test:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/tests');
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Create New Test
        </Typography>
        
        <Paper sx={{ p: 3, mt: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="testName"
              label="Test Name"
              name="testName"
              placeholder="Shown to candidates in their invitation email"
              autoFocus
              value={testName}
              onChange={(e) => setTestName(e.target.value)}
              error={!!nameError}
              helperText={nameError}
              disabled={isLoading}
            />
            
            <FormControl fullWidth margin="normal">
              <InputLabel id="time-limit-label">Time Limit</InputLabel>
              <Select
                labelId="time-limit-label"
                id="timeLimitMinutes"
                value={timeLimitMinutes}
                label="Time Limit"
                onChange={(e: SelectChangeEvent<number>) => setTimeLimitMinutes(e.target.value as number)}
                disabled={isLoading}
              >
                <MenuItem value={30}>30 minutes</MenuItem>
                <MenuItem value={45}>45 minutes (recommended)</MenuItem>
                <MenuItem value={60}>60 minutes</MenuItem>
                <MenuItem value={90}>90 minutes</MenuItem>
                <MenuItem value={120}>120 minutes</MenuItem>
              </Select>
            </FormControl>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                onClick={handleCancel}
                sx={{ mr: 1 }}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : null}
              >
                {isLoading ? 'Creating...' : 'Create Test'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default CreateTestPage;
